import { consume } from '@lit/context';
import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { ConfigurationDto, PriceBreakdownDto } from '@/api-sdk';
import { iconCart, iconEye } from '@/components/icons';
import {
  ConfiguratorContextType,
  GlobalComponentType,
  configuratorGlobal,
  global,
} from '@/context';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import {
  baseStyles,
  buttonStyles,
  loadingStyles,
  rootVariablesStyles,
  typographyStyles,
} from '@/styles';
import {
  ActionEvent,
  DateType,
  IntentActionEvent,
  IntentToEvent,
} from '@/types';
import { elephantOrderDateTemplate } from '@/utils';

import '../../drafts/configurator/fg-draft-save';

@customElement('fg-configurator-summary')
@localized()
export class FgConfiguratorSummary extends LitElement {
  /**
   * Object containing all data for the price breakdown
   *
   * @property priceBreakdown
   * @type {PriceBreakdownDto}
   */
  @property({ type: Object })
  priceBreakdown?: PriceBreakdownDto;

  /**
   * If the configuration is available we can check if the configuration is valid
   *
   * @property configuration
   * @type {ConfigurationDto}
   */
  @property({ type: Object })
  configuration?: ConfigurationDto | null;

  /**
   * To check if the print option is validated to fire the add to cart event
   *
   * @property quantityValidated
   */
  @property({ type: Boolean })
  quantityValidated = false;

  /**
   * String containing the error message that is shown in the summary when something went wrong when trying
   * to add the product to the cart
   */
  @property({ type: Boolean })
  error = false;

  /**
   * Boolean to show the loading state of the summary
   */
  @property({ type: Boolean })
  loading = false;

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  protected render() {
    const { salable, editing } = this.configuratorContext;
    const { customerId, locale, layout } = this.globalContext;

    return html`
      <div class="summary-wrapper ${classMap({ loading: this.loading })}">
        <h4>${t('labels.summary')}</h4>
        <section>
          ${this.priceBreakdown?.variants
            ? html`
                <ul class="summary-variants">
                  ${this.priceBreakdown.variants.map(variant => {
                    const {
                      color,
                      explanation,
                      totalPrice,
                      version,
                      size,
                      allInUnitPrice,
                    } = variant;
                    return html`
                      <li class="summary-item">
                        <strong>
                          ${color?.name} ${version?.name} ${size?.name}
                        </strong>
                        <div class="summary-line">
                          <span>${explanation}</span>
                          <span>${totalPrice.formatted}</span>
                        </div>
                        ${this.priceBreakdown?.printOptions &&
                        this.priceBreakdown?.printOptions.length > 0 &&
                        allInUnitPrice
                          ? html`
                              <div class="summary-line">
                                <span>
                                  ${allInUnitPrice.formatted}
                                  ${t('labels.pricePerUnit')}
                                </span>
                              </div>
                            `
                          : nothing}
                      </li>
                    `;
                  })}
                </ul>
              `
            : null}
          ${this.priceBreakdown?.printOptions
            ? html`
                <ul class="summary-print-options">
                  ${this.priceBreakdown.printOptions.map(
                    (printOption, index) => {
                      const { colors, setup } = printOption;
                      return html`
                        <li class="summary-item">
                          <strong>
                            ${t('labels.printNumber', {
                              index: index + 1,
                            })}
                          </strong>
                          <ul>
                            ${colors.map(
                              (color, index) => html`
                                <li class="summary-line">
                                  <span>
                                    ${t('labels.colors', { count: index + 1 })}
                                    (${color.explanation})
                                  </span>
                                  <span>${color.totalPrice.formatted}</span>
                                </li>
                              `,
                            )}
                            ${setup.totalPrice.formatted
                              ? html`
                                  <li class="summary-line">
                                    <span>
                                      ${t('labels.setupCosts')}
                                      ${setup.explanation
                                        ? html`
                                            (${setup.explanation})
                                          `
                                        : nothing}
                                    </span>
                                    <span>${setup.totalPrice.formatted}</span>
                                  </li>
                                `
                              : nothing}
                          </ul>
                        </li>
                      `;
                    },
                  )}
                </ul>
              `
            : null}
          ${this.priceBreakdown?.variants.some(e => e.surcharges.length > 0)
            ? html`
                <div class="summary-surcharges">
                  <strong>${t('labels.surcharges')}</strong>
                  <ul class="summary-item">
                    ${this.priceBreakdown?.variants.map(variant =>
                      variant.surcharges.map(
                        surcharge => html`
                          <li class="summary-line">
                            <span>
                              ${surcharge.name}
                              ${surcharge.explanation
                                ? html`
                                    (${surcharge.explanation})
                                  `
                                : nothing}
                            </span>
                            <span>${surcharge.totalPrice.formatted}</span>
                          </li>
                        `,
                      ),
                    )}
                  </ul>
                </div>
              `
            : nothing}
        </section>
        ${this.priceBreakdown?.handling?.totalPrice.formatted
          ? html`
              <section class="summary-handling">
                <span>${t('labels.processingCosts')}:</span>
                <span>
                  ${this.priceBreakdown?.handling?.totalPrice.formatted}
                </span>
              </section>
            `
          : null}
        ${this.priceBreakdown?.productionSpeedDiscount
          ? html`
              <div class="production-speed-discount">
                <span>
                  ${t(
                    layout === 'retail'
                      ? 'content.productionSpeed.productionSpeedDeliveryDiscount'
                      : 'content.productionSpeed.productionSpeedDiscount',
                  )}:
                </span>
                <span>
                  -${this.priceBreakdown?.productionSpeedDiscount?.formatted}
                </span>
              </div>
            `
          : nothing}
      </div>

      <section
        class="summary-totals 
          ${classMap({
          loading: this.loading,
          'no-price': !this.priceBreakdown?.totals,
          salable: salable,
        })}"
      >
        <div class="totals-excl-vat">
          <strong>${t('labels.priceExclVat')}:</strong>
          <span class="price">
            ${this.priceBreakdown?.totals?.excludingVat?.formatted || '-'}
          </span>
        </div>
        ${layout === 'retail'
          ? html`
              <div class="totals-incl-vat">
                <span>${t('labels.priceInclVat')}:</span>
                <span>
                  ${this.priceBreakdown?.totals?.includingVat?.formatted || '-'}
                </span>
              </div>
            `
          : nothing}
        ${this.priceBreakdown
          ? html`
              <div class="price-details">
                <button
                  class="button-link"
                  @click=${() =>
                    this.dispatchEvent(
                      new CustomEvent('open-price-details', { composed: true }),
                    )}
                >
                  ${iconEye} ${t('actions.viewPriceDetails')}
                </button>
              </div>
            `
          : nothing}
      </section>
      ${salable
        ? html`
            <section class="summary-buttons">
              <button
                class="button button-cta"
                @click=${() => {
                  if (this.configuration && this.quantityValidated) {
                    this.intentTo('intent-to-add-to-cart');
                  }
                  this.triggerValidationEvents('add-to-cart');
                }}
              >
                ${iconCart}
                ${editing
                  ? t('actions.updateCart')
                  : t('actions.addToShoppingCart')}
              </button>
              ${this.error
                ? html`
                    <p class="error">${t('errors.somethingWentWrong')}</p>
                  `
                : nothing}
              <div class="expected-shipping-container">
                ${elephantOrderDateTemplate(
                  this.priceBreakdown?.expectedShippingDate,
                  locale,
                  DateType.SHIPPING,
                  true,
                  this.configuratorContext.hspEnabled,
                )}
              </div>
              ${!editing && customerId
                ? html`
                    <fg-draft-save
                      part="wholesale"
                      .configuration=${this.configuration}
                      .isQuantityValidated=${this.quantityValidated}
                    ></fg-draft-save>
                  `
                : nothing}
            </section>
          `
        : nothing}
    `;
  }

  /**
   * Dispatches the add to cart intent to event to trigger all logic for adding a product to the cart in Commerce Engine
   *
   * @event intent-to
   */
  intentTo(actionEvent: IntentActionEvent) {
    this.dispatchEvent(
      new CustomEvent<IntentToEvent>('intent-to', {
        detail: {
          eventName: actionEvent,
        },
      }),
    );
  }
  /**
   * Dispatches event to trigger validation messages for the steps that are not valid
   *
   * @param actionEvent The action event that is triggered ("add-to-cart" | "add-to-quotation" | "request-print-proof" | "update-cart")
   */
  triggerValidationEvents(actionEvent: ActionEvent) {
    this.dispatchEvent(
      new CustomEvent<{ actionEvent: ActionEvent }>(
        'trigger-validation-events',
        {
          composed: true,
          detail: {
            actionEvent: actionEvent,
          },
        },
      ),
    );
  }

  static styles = [
    baseStyles,
    buttonStyles,
    typographyStyles,
    loadingStyles,
    rootVariablesStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        width: 100%;
        padding: 24px 0;
        max-height: 643px;
        border: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        margin-top: 56px;
      }

      .summary-wrapper {
        overflow: auto;
        padding: 0 24px;
      }

      .summary ul strong {
        font-weight: 500;
      }

      .summary-variants .summary-item,
      .summary-print-options .summary-item {
        padding: 8px 0;
        border-bottom: 1px solid
          var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .summary-item .summary-line:not(:last-of-type) {
        margin-bottom: 4px;
      }

      .summary-variants .summary-item:last-of-type,
      .summary-print-options .summary-item:last-of-type {
        border-bottom: none;
      }

      .summary-surcharges {
        padding: 8px 0;
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .summary-variants strong,
      .summary-print-options strong,
      .summary-surcharges strong {
        display: block;
        text-transform: capitalize;
        font-weight: 500;
        margin-bottom: 4px;
      }

      .summary-print-options .summary-item:first-of-type {
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .summary-line {
        display: flex;
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        justify-content: space-between;
        gap: 8px;
        font-size: 0.875rem;
        line-height: 1.375rem;
      }

      .summary-handling {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .summary-handling > span {
        display: flex;
        font-size: 0.875rem;
        line-height: 1.375rem;
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
      }

      .summary-totals {
        padding: 0 24px;
      }

      .summary-totals:not(.salable) {
        padding-bottom: 24px;
      }

      .summary-totals.no-price {
        margin-top: 0.5rem;
      }

      .summary-totals:not(.no-price) {
        padding-top: 0.5rem;
        border-top: 1px solid var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .summary-totals .totals-incl-vat,
      .summary-totals .totals-excl-vat {
        display: flex;
        justify-content: space-between;
      }

      .summary-totals .totals-excl-vat {
        font-size: 1rem;
        line-height: 1.9375rem;
      }

      .summary-totals .totals-excl-vat .price,
      .summary-totals .totals-excl-vat strong {
        font-size: 1rem;
        font-weight: 600;
        font-family: var(
          --fg-font-family--brand,
          var(--fg-fallback-font-family--brand)
        );
      }

      .summary-totals .totals-excl-vat .price {
        font-size: 1.375rem;
      }

      .summary-buttons {
        margin-top: 0.75rem;
        padding: 0 24px;
      }

      .summary-buttons button:not(.info-shipping-date) {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
      }

      .price-details {
        width: 100%;
        display: flex;
        justify-content: flex-end;
      }

      .price-details button {
        text-decoration: none;
        color: var(--fg-tertiary, var(--fg-fallback-tertiary));
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .error {
        font-size: 0.875rem;
        color: var(--fg-error--dark, var(--fg-fallback-error--dark));
        text-align: center;
        margin-top: 15px;
      }

      .shipping-date {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        margin-top: 16px;
        gap: 2px;
        position: relative;

        & date {
          display: flex;
          align-items: center;
          gap: 4px;
        }
      }

      .expected-shipping-container .expected-shipping-content {
        display: flex;
        flex-direction: row;
        gap: 4px;
      }

      .production-speed-discount {
        display: flex;
        justify-content: space-between;
        padding: 0 0 8px 0;
        & span {
          color: var(--fg-error, var(--fg-fallback-error));
          font-size: 0.875rem;
          line-height: 1.375rem;
        }
      }

      .info-shipping-date {
        display: flex;
        color: var(--fg-secondary, var(--fg-fallback-secondary));
      }

      @media screen and (max-width: ${BREAKPOINT_MEDIUM_PX - 1}px) {
        :host {
          margin: 0;
          padding: 0;
          border: 0;
        }
        .summary-wrapper,
        .summary-totals,
        .summary-buttons,
        .shipping-date {
          display: none;
        }
      }
    `,
  ];
}
